import { useFormSessionLocals } from '@form-engine/core-application/hooks/use-formsession';
import { ElementDefinition } from '@form-engine/core-application/types/form';
import { ComponentProps, useMemo } from 'react';
import { FormControl } from 'react-bootstrap';
import { day } from '@form-engine/core-util/day';
import { compile } from '@form-engine/core-util/template';

/**
 * parses a date provided via template into a Date object and than adds one day
 * to it, providing the _minimum_ date, that can be selected in the date picker.
 * finally returns in the ISO Date (w/o time) format required by datepicker inputs.
 * @param template - The template containing some form of date representation e.g. `{{date "now"}}`
 * @param locals - variable stack to use during template compilation
 * @returns ISO Date string
 */
function getMin(template: unknown, locals?: Record<string, unknown>) {
    const date = new Date(compile(String(template), locals));

    if (isNaN(date.getTime())) {
        return '';
    }

    return day(date).add(1, 'day').format('YYYY-MM-DD');
}

/**
 * parses a date provided via template into a Date object and than substracts one day
 * from it, providing the _maximum_ date, that can be selected in the date picker.
 * finally returns in the ISO Date (w/o time) format required by datepicker inputs.
 * @param template - The template containing some form of date representation e.g. `{{date "now"}}`
 * @param locals - variable stack to use during template compilation
 * @returns ISO Date string
 */
function getMax(template: unknown, locals?: Record<string, unknown>) {
    const date = new Date(compile(String(template), locals));

    if (isNaN(date.getTime())) {
        return '';
    }

    return day(date).subtract(1, 'day').format('YYYY-MM-DD');
}

/**
 * provides date (and datetime) html input attributes, to control selectable date ranges (min,max)
 * based on a elements definition
 * @param element - definition
 * @returns attributes object
 */
export default function useDateElementAttributes(element?: ElementDefinition) {
    const locals = useFormSessionLocals();
    return useMemo(() => {
        const attrs: Partial<ComponentProps<typeof FormControl>> = {};

        if (!['date', 'datetime'].includes(String(element?.inputType))) {
            return attrs;
        }

        if (element?.validateContentTypeDateAfter) {
            attrs.min = getMin(element?.validateContentTypeDateAfter, locals);
        }

        if (element?.validateContentTypeDateBefore) {
            attrs.max = getMax(element?.validateContentTypeDateBefore, locals);
        }

        return attrs;
    }, [element, locals.state]);
}
